import { useCallback, useMemo, useState } from "react";
import { success } from "@";
import { useUrlSearch } from "@/shared/hooks/useUrlSearch.ts";
import { useUnassignedStagesQuery } from "../api/queries";
import {
  useAssignDriverMutation,
  useCreateManualVerificationMutation,
  useIgnoreVerificationMutation,
} from "../api/mutations";
import { donationsSearchValues } from "../lib/options.ts";
import { ignoreOrdersConfirmDialog } from "../lib/helpers.ts";
import type {
  DonationsQueryParams,
  DonationsSearchValues,
  PaginationState,
  UnassignedVerificationStage,
} from "../types.ts";

interface UseDonationsPageReturn {
  data: UnassignedVerificationStage[];
  isLoading: boolean;
  error: Error | null;
  totalItems: number;
  // Search
  search: { show: boolean; values: DonationsSearchValues };
  updateSearch: (params: {
    show?: boolean;
    values?: DonationsSearchValues;
  }) => void;
  handleSearchSubmit: () => void;
  handleSearchReset: () => void;
  // Pagination
  pg: PaginationState;
  updatePg: (newPg: Partial<PaginationState>) => void;
  // Selection
  selectedIds: number[];
  selectingAll: boolean;
  handleSelect: (params: {
    selectedRows: UnassignedVerificationStage[];
  }) => void;
  clearSelection: () => void;
  setSelectingAll: (value: boolean) => void;
  selectionResetKey: number;

  // Actions
  refetch: () => void;
  assignDriver: (driverId: string, ids: number[]) => Promise<void>;
  handleIgnoreFromVerification: () => Promise<void>;
  isIgnoring: boolean;
  isAssigning: boolean;
  handleCreateManualVerification: (orderId: number) => Promise<void>;
  isCreatingManual: boolean;
}

export const useDonationsPage = (): UseDonationsPageReturn => {
  const {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    page,
    size,
    setPage,
    setSize,
    showFilters,
    setShowFilters,
  } = useUrlSearch<DonationsSearchValues>({
    defaultValues: donationsSearchValues,
  });

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectingAll, setSelectingAll] = useState<boolean>(false);
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  // Query with URL-based params
  const queryParams: DonationsQueryParams = useMemo(
    () => ({
      page,
      perSize: size,
      search: appliedValues,
    }),
    [page, size, appliedValues],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useUnassignedStagesQuery(queryParams);

  const data = queryData?.data || [];
  const totalItems = queryData?.totalItems || 0;

  // Mutations
  const assignDriverMutation = useAssignDriverMutation();
  const ignoreVerificationMutation = useIgnoreVerificationMutation();
  const createManualVerificationMutation =
    useCreateManualVerificationMutation();

  // Handlers
  const updatePg = useCallback(
    (newPg: Partial<PaginationState>) => {
      if (newPg.page !== undefined) setPage(newPg.page);
      if (newPg.size !== undefined) setSize(newPg.size);
    },
    [setPage, setSize],
  );

  const updateSearch = useCallback(
    (updates: { show?: boolean; values?: DonationsSearchValues }) => {
      if (updates.show !== undefined) setShowFilters(updates.show);
      if (updates.values) {
        Object.entries(updates.values).forEach(([key, value]) => {
          updateDraft(key as keyof DonationsSearchValues, value);
        });
      }
    },
    [setShowFilters, updateDraft],
  );

  const handleSelect = useCallback(
    (params: { selectedRows: UnassignedVerificationStage[] }) => {
      const ids = params.selectedRows.map((row) => row.id);
      setSelectedIds(ids);
      setSelectingAll(false);
    },
    [setSelectingAll],
  );

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectingAll(false);
    setSelectionResetKey((prev) => prev + 1);
  }, []);

  const assignDriver = useCallback(
    async (driverId: string, ids: number[]) => {
      await assignDriverMutation.mutateAsync({
        driverId,
        verificationStages: ids,
        selectingAll: selectingAll,
        searchValues: draftValues,
      });
    },
    [assignDriverMutation, draftValues, selectingAll],
  );

  const handleCreateManualVerification = useCallback(
    async (orderId: number) => {
      try {
        await createManualVerificationMutation.mutateAsync(orderId);
      } catch (error) {
        console.error("handleCreateManualVerification:error", error);
      }
    },
    [createManualVerificationMutation],
  );

  const handleIgnoreFromVerification = useCallback(async () => {
    const confirmed = await ignoreOrdersConfirmDialog();
    if (!confirmed) return;

    await ignoreVerificationMutation.mutateAsync({
      verificationStages: selectedIds,
      searchValues: draftValues,
      selectingAll,
    });
    await success("تم استبعاد الطلبات بنجاح");
    clearSelection();
  }, [selectedIds, ignoreVerificationMutation, clearSelection]);

  return {
    data,
    isLoading,
    error: error as Error | null,
    totalItems,
    // Search (for compatibility with existing components)
    search: { show: showFilters, values: draftValues },
    updateSearch,
    handleSearchSubmit: applySearch,
    handleSearchReset: resetSearch,
    // Pagination (for compatibility with existing components)
    pg: { page, size },
    updatePg,
    // Selection
    selectedIds,
    selectingAll,
    handleSelect,
    clearSelection,
    setSelectingAll,
    // Actions
    refetch,
    assignDriver,
    handleIgnoreFromVerification,
    isIgnoring: ignoreVerificationMutation.isPending,
    isAssigning: assignDriverMutation.isPending,
    handleCreateManualVerification,
    isCreatingManual: createManualVerificationMutation.isPending,
    selectionResetKey,
  };
};
